<template>
  <v-list-item
    :value="path"
    :style="{ paddingLeft: `${depth * 16 + 4}px !important` }"
    class="file-tree-item"
    :class="{ 'opacity-50': isDomainItemPrompt && itemInfo.originalPrompt === itemInfo.currentPrompt }"
  >
    <v-list-item-title>{{ name }}</v-list-item-title>
    <template v-slot:prepend>
      <v-icon v-if="item.isFolder" size="16">{{ item.isOpened ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
      <span v-else class="pl-4"></span>
      <v-icon size="16">
        {{
          (isDomainItemPrompt || isBasePrompt) && itemInfo.originalPrompt !== itemInfo.currentPrompt ?
            'mdi-file-edit-outline' : visibleIcon
        }}
      </v-icon>
    </template>
    <template v-slot:append>
      <div class="d-flex align-center ga-1">
        <v-icon v-if="isDirty" class="small" color="warning">mdi-circle</v-icon>
        <v-menu location="bottom" :offset="0">
          <template #activator="{ props }">
            <v-btn
              class="show-on-hover-item"
              v-bind="props"
              size="small"
              variant="plain"
              tabindex="-1"
              icon="mdi-dots-vertical"
              :style="{ 'cursor': 'pointer' }"
            />
          </template>
          <template v-slot:default>
            <v-list density="compact" class="pt-0">
              <v-list-item
                v-for="item in (isFolder ? folderMenuItems : fileMenuItems)"
                :key="item.label"
                class="py-0 min-h-0"
                @click="item.action"
              >
                <v-list-item-title class="text-sm d-flex align-center ga-1">
                  <v-icon size="16" class="pa-0">{{ item.icon }}</v-icon>
                  <span>{{ item.label }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </template>
        </v-menu>
      </div>
    </template>
    <v-tooltip
      v-if="isDomainItemPrompt || isBasePrompt"
      activator="parent"
      location="right"
      max-width="400px"
    >
      <span v-if="isDomainItemPrompt">
        Prompt for "{{ itemInfo?.name }}" {{ itemInfo?.type }}
        ({{ itemInfo.originalPrompt === itemInfo.currentPrompt ? 'not modified' : 'modified' }}).
      </span>
      <span v-else>
        Base prompt template for {{ itemInfo?.type }}
        ({{ itemInfo.originalPrompt === itemInfo.currentPrompt ? 'not modified' : 'modified' }}).
      </span>
    </v-tooltip>
  </v-list-item>
</template>

<script setup>
import { computed, inject } from 'vue'
import {
  CODE_EDITOR_APPLY_PATCH_FILE,
  CODE_EDITOR_APPLY_PATCH_FOLDER,
  CODE_EDITOR_OPEN_FILE,
  CODE_EDITOR_SAVE_FILE,
  CODE_EDITOR_DOWNLOAD_FILE,
  CODE_EDITOR_DELETE_FILE,
  CODE_EDITOR_DELETE_FOLDER,
  CODE_EDITOR_COPY_PROMPT
} from '@/codegen/constants'
import {
  AI_USER_PROMPT_COMMAND_NODEJS,
  AI_USER_PROMPT_ENTITY_NODEJS,
  AI_USER_PROMPT_GWT_NODEJS,
  AI_USER_PROMPT_READMODEL_NODEJS
} from '@/ai/prompts/markdown'

const {
  item,
  fileObjects
} = defineProps({
  item: { type: Object },
  fileObjects: { type: Object }
})

const { name, path, isFolder, depth } = item

const iconMap = {
  'open-folder': 'mdi-folder-open-outline',
  'close-folder': 'mdi-folder-outline',
  'open-file': 'mdi-file',
  'close-file': 'mdi-file-outline'
}
const templatePaths = [
  '.qlerify/entity-template.md',
  '.qlerify/command-template.md',
  '.qlerify/readmodel-template.md',
  '.qlerify/gwt-template.md'
]

const visibleIcon = computed(() =>
  iconMap[
    `${item.isOpened ? 'open' : 'close'}-${item.isFolder ? 'folder' : 'file'}`
  ]
)

const isDirty = computed(() => {
  const keys = Object.keys(fileObjects)
  const filteredKeys = keys.filter(key => key.startsWith('root/' + path))

  return filteredKeys.some(key => fileObjects[key].draftContents !== null)
})
const isBasePrompt = computed(() => templatePaths.includes(item.path))
const isDomainItemPrompt = computed(() => !item.isFolder && item.path.includes('.qlerify') && !templatePaths.includes(item.path))

const itemInfo = computed(() => {
  if (item.isFolder) {
    return
  }

  const promptTemplates = {
    entity: AI_USER_PROMPT_ENTITY_NODEJS,
    command: AI_USER_PROMPT_COMMAND_NODEJS,
    readmodel: AI_USER_PROMPT_READMODEL_NODEJS,
    gwt: AI_USER_PROMPT_GWT_NODEJS
  }

  const filename = item.name.replace('.md', '')
  const parts = filename.split('-')
  const type = parts.shift()
  const name = parts.join(' ')

  return { type, name, originalPrompt: promptTemplates[type], currentPrompt: fileObjects?.[`root/${item.path}`]?.contents }
})

const treeAction = inject('treeAction', (data) => {
  console.warn('doDomainAction not provided', data)
})

// // Constants
const folderMenuItems = [
  { label: 'Apply', icon: 'mdi-file-compare', action: () => treeAction({ type: CODE_EDITOR_APPLY_PATCH_FOLDER, path, name }) },
  // { label: 'Rename', icon: 'mdi-pen', action: () => treeAction({ type: 'rename-folder', path, name }) },
  // { label: 'Search this directory', icon: 'mdi-file-search', action: () => treeAction('search-folder') },
  // { label: 'Add file', icon: 'mdi-file-plus', action: () => treeAction('add-file-to-folder') },
  // { label: 'Add folder', icon: 'mdi-folder-plus', action: () => treeAction('add-folder-to-folder') },
  // { label: 'Copy folder path', icon: 'mdi-clipboard', action: () => treeAction('copy-path-of-folder') },
  { label: 'Delete', icon: 'mdi-delete', action: () => treeAction({ type: CODE_EDITOR_DELETE_FOLDER, path }) }
]

const fileMenuItems = [
  { label: 'Apply', icon: 'mdi-file-compare', action: () => treeAction({ type: CODE_EDITOR_APPLY_PATCH_FILE, path, name }) },
  // { label: 'Rename', icon: 'mdi-pen', action: () => treeAction('rename-file') },
  { label: 'Save File', icon: 'mdi-content-save', action: () => treeAction({ type: CODE_EDITOR_SAVE_FILE, path }) },
  { label: 'Open', icon: 'mdi-eye', action: () => treeAction({ type: CODE_EDITOR_OPEN_FILE, path }) },
  { label: 'Download', icon: 'mdi-download', action: () => treeAction({ type: CODE_EDITOR_DOWNLOAD_FILE, path, name }) },
  ...(!item.isFolder && item.path?.includes('.qlerify/')
    ? [{ label: 'Copy Final Prompt', icon: 'mdi-content-copy', action: () => treeAction({ type: CODE_EDITOR_COPY_PROMPT, path, name }) }]
    : []),
  { label: 'Delete', icon: 'mdi-delete', action: () => treeAction({ type: CODE_EDITOR_DELETE_FILE, path }) }
]

// // Computed
// const filepath = computed(() => path + name)

// const isLeafItem = computed(() =>
//   !children || Object.keys(children).length === 0
// )

// const isDirtyFolder = computed(() =>
//   dirtyFolders.has(filepath.value)
// )

// const childEntries = computed(() => {
//   const entries = Object.entries({ ...children })
//   // Folders first, then alphabetical
//   entries.sort((a, b) => a[0].localeCompare(b[0]))
//   entries.sort((a, b) => Object.keys(b[1]).length - Object.keys(a[1]).length)
//   return entries
// })

// const fileObject = computed(() =>
//   objects?.[filepath.value]
// )

// const thisOpened = computed(() =>
//   !isLeafItem.value &&
//   opened.includes(filepath.value)
// )

// const isFileEmpty = computed(() =>
//   !Object.keys(objects ?? {}).includes(filepath.value) &&
//   name !== '__template'
// )
</script>

<style scoped>
v-list {
  --prepend-width: 16px;
}
</style>

<style scoped>
.file-tree-group {
  --prepend-width: 0px !important;
  /* padding-inline-start: var(--indent-padding) !important; */
}

.v-list > .file-tree-group {
  --prepend-width: -16px !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px !important;
  border-radius: 4px !important;
}

.file-tree-item {
  .show-on-hover-item {
    /* display: none !important; */
    opacity: 0 !important;
  }

  &:hover {
    .show-on-hover-item {
      /* display: block !important; */
      opacity: 1 !important;
    }
  }
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline: 4px !important;
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line .file-tree-item {
  padding-inline: 16px !important;
}

.v-list-item--density-compact.v-list-item--one-line {
  min-height: 28px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}

.file-tree-item {
  padding: 0 4px !important;
  height: 24px !important;
  min-height: 24px !important;
  font-size: 12px !important;
  padding-inline-start: var(--indent-padding) !important;
  /* padding-inline-start: 16px !important; */
  /* --indent-padding: calc(8px + var(--indent-padding)); */
  /* padding-inline-start: calc(2px + var(--indent-padding)) !important; */
}

.file-tree-item .v-btn {
  width: 20px;
  height: 20px;
}

.file-tree-item .v-icon {
  font-size: 20px;
  padding-left: 0px;
}

.file-tree-item .v-icon.small {
  padding-left: 0px;
  font-size: 10px;
}

.file-tree-item .v-list-item__prepend {
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  height: 24px;
}

.file-tree-item .v-list-item__append {
  width: 28px;
  min-width: 28px;
  max-width: 28px;
}
</style>
